export const CASE_STUDY_ORIGIN = "https://www.vertexclimate.co.uk";
export const CASE_STUDIES_PATH = "/case-studies";

const img = (n: number) => `/api/project-images/kings-gym-${String(n).padStart(2, "0")}.webp`;
const chessingtonImg = (n: number) => `/api/project-images/kings-gym-chessington-${String(n).padStart(2, "0")}.webp`;

export function withBase(path: string, basePath = import.meta.env?.BASE_URL || "/") {
  if (/^https?:/.test(path) || path.startsWith("/api/")) return path;
  return basePath.replace(/\/$/, "") + path;
}

export type CaseStudyPhoto = { src: string; alt: string; caption: string; stage?: string };
export type CaseStudy = {
  slug: string;
  path: string;
  title: string;
  shortTitle: string;
  description: string;
  image: string;
  imageAlt: string;
  client: string;
  location: string;
  service: string;
  serviceHref: string;
  serviceLabel: string;
  quoteHref: string;
  summary: string;
  facts: { label: string; value: string }[];
  scope: string[];
  stageNote?: string;
  photos: CaseStudyPhoto[];
};

const crawleyPhotos: CaseStudyPhoto[] = [
  { src: img(1), alt: "Timber sauna cabin frame under construction inside Kings Gym Crawley", caption: "Timber cabin taking shape in the gym, with the door opening framed.", stage: "Installation stage" },
  { src: img(2), alt: "Front of the part-built timber sauna structure among gym equipment", caption: "Front elevation during construction, built in place within the existing space.", stage: "Installation stage" },
  { src: img(3), alt: "Close view of the sauna entrance opening and timber wall boarding", caption: "Entrance opening and timber boarding before internal fit-out.", stage: "Installation stage" },
  { src: img(4), alt: "Wider view of the sauna structure under construction below overhead ducting", caption: "Wider view showing the structure below the existing overhead services.", stage: "Installation stage" },
];

const croydonPhotos: CaseStudyPhoto[] = [
  { src: img(16), alt: "Ceiling cassette air conditioning units in the main gym at Kings Gym Croydon", caption: "Main gym floor with ceiling cassettes installed among the roof steelwork." },
  { src: img(17), alt: "Close view of a four-way ceiling cassette mounted below the gym roof", caption: "Four-way cassette mounted to throw air across the training floor." },
  { src: img(18), alt: "Ceiling cassettes above weights equipment in the main gym", caption: "Cassettes positioned above the free-weights area." },
  { src: img(19), alt: "Ceiling cassette with insulated pipework below the gym roof", caption: "Cassette with its pipework routed neatly along the structure." },
  { src: img(6), alt: "Outdoor condenser unit wall-mounted on bracket against cladding", caption: "Outdoor unit wall-mounted on brackets against the building cladding." },
  { src: img(7), alt: "Two outdoor units mounted high on a brick wall with trunking", caption: "Paired outdoor units mounted high, with trunked pipe routes." },
  { src: img(8), alt: "Outdoor units and trunking runs on the rear elevation", caption: "Trunking carries refrigerant and power runs along the wall." },
  { src: img(9), alt: "Outdoor unit on cladding seen along the side of the building", caption: "Outdoor unit along the side elevation." },
  { src: img(10), alt: "Close view of an outdoor unit on wall bracket", caption: "Bracket-mounted condenser with clearance for airflow." },
  { src: img(11), alt: "Outdoor unit mounted on the clad side wall", caption: "Condenser positioned clear of ground-level activity." },
  { src: img(12), alt: "Outdoor unit on bracket above a yard area", caption: "Mounted above the yard to keep access routes free." },
  { src: img(13), alt: "Outdoor unit seen from the yard along the cladding", caption: "View from the yard along the external wall." },
  { src: img(14), alt: "Outdoor units along the side of the building", caption: "Outdoor units spaced along the elevation." },
  { src: img(15), alt: "Outdoor units on the building wall near the yard", caption: "Completed external positions from the far end of the yard." },
];

export const caseStudies: CaseStudy[] = [
  {
    slug: "kings-gym-chessington-air-conditioning",
    path: "/case-studies/kings-gym-chessington-air-conditioning",
    title: "Kings Gym Chessington: 8 AC Units Installed | VertexAir",
    shortTitle: "Eight air conditioning units at Kings Gym",
    description: "Eight air conditioning units installed by VertexAir at Kings Gym Chessington. Explore photographs of the indoor cassettes, outdoor units and installation work.",
    image: chessingtonImg(1),
    imageAlt: "Ceiling cassette air conditioning in the finished reception and seating area at Kings Gym Chessington",
    client: "Kings Gym",
    location: "Chessington",
    service: "Air conditioning installation",
    serviceHref: "/installation",
    serviceLabel: "Air conditioning installation",
    quoteHref: "/quote",
    summary: "VertexAir installed eight air conditioning units at Kings Gym Chessington. This project gallery brings together the finished reception area, indoor ceiling cassettes and outdoor equipment, alongside photographs taken during the installation.",
    facts: [
      { label: "Client", value: "Kings Gym" },
      { label: "Site", value: "Chessington" },
      { label: "Service", value: "Air conditioning installation" },
      { label: "Units", value: "Eight installed" },
    ],
    scope: [
      "Eight air conditioning units installed at Kings Gym Chessington",
      "Ceiling cassette units pictured in the reception and gym spaces",
      "Wall-mounted outdoor equipment on the building’s brick exterior",
      "Project photographs documenting installation stages and the finished reception area",
    ],
    photos: [
      { src: chessingtonImg(1), alt: "Reception and seating area with ceiling cassette units at Kings Gym Chessington", caption: "Finished reception and seating area with ceiling cassettes set below the dark roof structure." },
      { src: chessingtonImg(5), alt: "Ceiling cassettes above a room being fitted out at Kings Gym Chessington", caption: "Indoor cassettes pictured while the surrounding room was being fitted out.", stage: "Installation stage" },
      { src: chessingtonImg(2), alt: "Two wall-mounted Fujitsu outdoor units with one service panel open", caption: "Outdoor equipment on the brick elevation during installation, with a service panel open.", stage: "Installation stage" },
      { src: chessingtonImg(3), alt: "Two outdoor air conditioning units mounted above a window", caption: "Paired outdoor units mounted on brackets above a window." },
      { src: chessingtonImg(4), alt: "Outdoor condenser on a brick wall beside temporary access scaffolding", caption: "Outdoor unit with temporary access equipment still in place.", stage: "Installation stage" },
      { src: chessingtonImg(7), alt: "Ceiling cassette above gym equipment with protective covering", caption: "Ceiling cassette above gym equipment during fit-out.", stage: "Installation stage" },
      { src: chessingtonImg(8), alt: "Ceiling cassette in a dark-walled gym room during fit-out", caption: "Another view of the indoor cassette and room during fit-out.", stage: "Installation stage" },
      { src: chessingtonImg(9), alt: "Ceiling cassette above exercise machines and mirrored gym walls", caption: "Indoor unit above the gym equipment, photographed during the works.", stage: "Installation stage" },
      { src: chessingtonImg(10), alt: "Gym equipment beneath a ceiling cassette with works in progress", caption: "A wider view of the cassette within the training space during fit-out.", stage: "Installation stage" },
      { src: chessingtonImg(11), alt: "Ceiling cassettes above a mirrored gym room with tools and materials on the floor", caption: "Room fit-out in progress beneath the installed ceiling cassettes.", stage: "Installation stage" },
      { src: chessingtonImg(6), alt: "Pool area at Kings Gym Chessington with the pool empty", caption: "Site context: the pool area. This photograph does not show the air conditioning installation." },
    ],
  },
  {
    slug: "kings-gym-croydon-air-conditioning",
    path: "/case-studies/kings-gym-croydon-air-conditioning",
    title: "Kings Gym Croydon air conditioning case study | VertexAir",
    shortTitle: "Five air conditioning units for the main gym",
    description: "VertexAir installed five air conditioning units inside the main gym at Kings Gym Croydon. Project photographs of the indoor cassettes and outdoor units.",
    image: img(17),
    imageAlt: croydonPhotos[1].alt,
    client: "Kings Gym",
    location: "Croydon",
    service: "Air conditioning installation",
    serviceHref: "/installation",
    serviceLabel: "Air conditioning installation",
    quoteHref: "/quote?service=" + encodeURIComponent("Air Conditioning Installation"),
    summary: "Five air conditioning units installed inside the main gym at Kings Gym Croydon. The photographs show the ceiling cassettes on the training floor and the outdoor units mounted on the building exterior.",
    facts: [
      { label: "Client", value: "Kings Gym" },
      { label: "Site", value: "Croydon" },
      { label: "Service", value: "Air conditioning installation" },
      { label: "Units", value: "Five, inside the main gym" },
    ],
    scope: [
      "Five indoor units installed inside the main gym",
      "Ceiling cassettes fixed below the existing roof structure",
      "Outdoor units wall-mounted on the building exterior",
      "Pipe and cable routes run in trunking externally",
    ],
    photos: croydonPhotos,
  },
  {
    slug: "kings-gym-crawley-sauna",
    path: "/case-studies/kings-gym-crawley-sauna",
    title: "Kings Gym Crawley sauna installation case study | VertexAir",
    shortTitle: "Sauna installation inside a working gym",
    description: "VertexAir installed a sauna at Kings Gym Crawley. Installation-stage photographs of the timber sauna structure being built on site.",
    image: img(1),
    imageAlt: crawleyPhotos[0].alt,
    client: "Kings Gym",
    location: "Crawley",
    service: "Sauna installation",
    serviceHref: "/sauna/install",
    serviceLabel: "Sauna installation",
    quoteHref: "/quote?category=sauna&service=" + encodeURIComponent("Sauna — Install"),
    summary: "A sauna installed at Kings Gym Crawley. These photographs were taken during construction and show the timber structure being built in place, not the finished room.",
    stageNote: "All photographs on this page were taken during installation. They show construction in progress, not the completed sauna.",
    facts: [
      { label: "Client", value: "Kings Gym" },
      { label: "Site", value: "Crawley" },
      { label: "Service", value: "Sauna installation" },
      { label: "Photos", value: "Installation stage" },
    ],
    scope: [
      "Sauna installed at the gym",
      "Timber structure built in place within the existing space",
      "Photographed during construction",
    ],
    photos: crawleyPhotos,
  },
];

export function findCaseStudy(slug: string) {
  return caseStudies.find(s => s.slug === slug);
}

export const caseStudiesIndexMeta = {
  path: CASE_STUDIES_PATH,
  title: "Case studies | Gym air conditioning and sauna projects | VertexAir",
  description: "Real VertexAir project photographs: eight AC units at Kings Gym Chessington, five units at Croydon and a sauna installation at Crawley.",
  image: img(17),
};
